import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Star, ChevronLeft, ChevronRight } from 'lucide-react'
import Reveal from '../components/Reveal.jsx'

const RAW = Array.isArray(window.wpData?.testimonials) ? window.wpData.testimonials : []

const THEME_URL = (window.wpData?.themeUrl ?? '').replace(/\/$/, '')

const OTA_LOGOS = {
  tripadvisor: `${THEME_URL}/images/ota/tripadvisor.svg`,
  booking:     `${THEME_URL}/images/ota/bookingcom.svg`,
  agoda:       `${THEME_URL}/images/ota/agoda.svg`,
  trip:        `${THEME_URL}/images/ota/tripcom.svg`,
  traveloka:   `${THEME_URL}/images/ota/traveloka.svg`,
}

const MOCK = [
  { review_content: 'Absolutely breathtaking. The crew was attentive without being intrusive — a rare balance in luxury travel.', rating: 5, ota_source: 'tripadvisor', name: 'James & Emily', nationality: 'United Kingdom' },
  { review_content: 'Ha Long Bay is stunning enough, but LJN made the entire journey feel effortless and personal. Every detail was considered.', rating: 5, ota_source: 'booking', name: 'Sophie Laurent', nationality: 'France' },
  { review_content: 'We have cruised the Mediterranean, the Nile, and the Mekong — this stands among the finest experiences of our lives.', rating: 5, ota_source: 'tripadvisor', name: 'Richard Chen', nationality: 'Singapore' },
  { review_content: 'The food alone was worth the trip. Exceptional service, stunning scenery, and memories we will carry forever.', rating: 5, ota_source: 'agoda', name: 'Mia Hoffmann', nationality: 'Germany' },
  { review_content: 'LJN Cruise redefined what we thought a cruise could be. Intimate, elegant, and deeply local in the best way.', rating: 5, ota_source: 'trip', name: 'Yuki Tanaka', nationality: 'Japan' },
  { review_content: 'From the moment we boarded to our final sunrise on the bay, every moment was perfectly curated.', rating: 5, ota_source: 'traveloka', name: 'Andi Pratama', nationality: 'Indonesia' },
]

const LIST = RAW.length ? RAW : MOCK

function OtaLogo({ source }) {
  const src = OTA_LOGOS[source]
  if (!src) return <span className="hp-testi-card__ota-text">{source}</span>
  return <img src={src} alt={source} className="hp-testi-card__ota-img" />
}

function StarRow({ count }) {
  const n = Math.min(5, Math.max(0, parseInt(count) || 0))
  return (
    <div className="hp-testi-card__stars">
      {Array.from({ length: n }, (_, i) => (
        <Star key={i} size={13} fill="var(--color-gold)" strokeWidth={0} color="var(--color-gold)" />
      ))}
    </div>
  )
}

export default function TestimonialsSection() {
  const scrollRef = useRef(null)
  const autoRef   = useRef(null)

  const scroll = useCallback((dir) => {
    const el = scrollRef.current
    if (!el) return
    const card = el.querySelector('.hp-testi-card')
    const step = card ? card.offsetWidth + 24 : 360
    const maxScroll = el.scrollWidth - el.clientWidth
    const next = el.scrollLeft + dir * step
    if (next > maxScroll) {
      el.scrollTo({ left: 0, behavior: 'smooth' })
    } else if (next < 0) {
      el.scrollTo({ left: maxScroll, behavior: 'smooth' })
    } else {
      el.scrollBy({ left: dir * step, behavior: 'smooth' })
    }
    resetAuto()
  }, [])

  const resetAuto = useCallback(() => {
    clearInterval(autoRef.current)
    autoRef.current = setInterval(() => scroll(1), 5000)
  }, [scroll])

  useEffect(() => {
    resetAuto()
    return () => clearInterval(autoRef.current)
  }, [resetAuto])

  return (
    <section className="hp-section hp-section--offwhite hp-testi-section">
      <div className="hp-inner">
        <Reveal variant="fade-up">
          <div className="hp-eyebrow">Guest Reviews</div>
          <h2 className="hp-title">Voices From the Bay</h2>
          <div className="hp-divider" />
        </Reveal>

        <div className="hp-testi__carousel" ref={scrollRef}>
          {LIST.map((t, i) => (
            <div key={i} className="hp-testi-card">
              <StarRow count={t.rating} />
              <p className="hp-testi-card__quote">"{t.review_content}"</p>
              <div className="hp-testi-card__footer">
                <div className="hp-testi-card__ota">
                  <OtaLogo source={t.ota_source} />
                </div>
                <div className="hp-testi-card__author">
                  <span className="hp-testi-card__name">{t.name}</span>
                  {t.nationality && <span className="hp-testi-card__nat">{t.nationality}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hp-testi__nav">
          <button className="hp-itin__btn" onClick={() => scroll(-1)} aria-label="Previous">
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button className="hp-itin__btn" onClick={() => scroll(1)} aria-label="Next">
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  )
}
